<!--
  Zimobi fork of `app/javascript/dashboard/components-next/sidebar/Sidebar.vue`.

  Wired in `custom/dashboard/routes/dashboard/Dashboard.vue` (itself a fork),
  which imports this file instead of the upstream one. The upstream file is left untouched so merges with
  Chatwoot stay clean; the flip side is that upstream changes to the sidebar
  (new menu groups, renamed icons, refactors) have to be re-ported here by hand.

  Delta against upstream — keep this list in sync when you change something:

  1. Root `<aside>` is `hidden sm:flex` instead of `flex`. Below 640px the
     BottomTabBar (`flex sm:hidden`) is the navigation, so the sidebar must not
     stack on top of `<main>`.
  2. Conversation → All also matches `lost_lead_distribution`, so the group stays
     highlighted on the lost lead screen (reached from notifications; the route
     needs a `:lead_distribution_id`, so it can never be a static link).
  3. Settings has two extra entries, each behind a gate the v4 sidebar cannot
     express through `meta.permissions` / `meta.featureFlag`:
     - Forms (with the visit hours), gated by the `cards` account attribute.
     - Funis (the names each board gives a closed card), under the same gate.
     - Client Settings, gated by `currentUser.type === 'SuperAdmin'`.
  4. The Contacts group is dropped for users with `hide_contacts_menu`.
  5. A "Create a Contact" action button takes the Contacts group's slot for
     custom roles that cannot manage contacts (the corretores). It is the v4
     counterpart of the `createContact` action item in the v3 primary menu, and
     the two are mutually exclusive by construction: whoever sees the Contacts
     group does not see the button, and vice versa.
  6. An "Imóveis" group sits above Campaigns, gated by the
     `rms_properties_listing` account attribute and restricted to account
     administrators. It embeds the RMS property listing and, like the entries
     in item 3, its account gate cannot be expressed through
     `meta.permissions` / `meta.featureFlag`.
  7. Entries upstream ships that Zimobi does not use are dropped: Contacts →
     Active (online presence, only ever populated by the website widget),
     Reports → CSAT / SLA / Bots, and Campaigns → SMS.
  8. A "Funis" group sits right below Conversations, gated by the `cards` account
     attribute: one entry per active board (Chamados, Leads locação, Leads venda,
     the agency's own), each opening that board.
  9. The notification bell is back in the footer. Upstream shipped it there
     behind a `<div v-if="false">` and then deleted it (commit b116ab5ad3),
     which is why that section is still `justify-between` with a single child.
     Zimobi keeps the Inbox group hidden (its route is gated by the
     `inbox_view` flag), and the bell is what replaces it as the way into the
     notification panel — otherwise v4 would have no in-app notifications UI at
     all, since the v3 sidebar's bell has no v4 counterpart.
  10. Features hidden by product decision are not listed: Captain, Companies,
      Settings → Security (SAML) and Settings → Agent Assignment, as well as
      the Chatwoot Cloud changelog card.

  Items 2-6 mirror the v3 sidebar, whose equivalents live in
  `components/layout/config/sidebarItems/` and
  `components/layout/sidebarComponents/atendeTotal/Secondary/atendeTotalSecondaryMixin.js`.

  Sibling components and composables are imported from the upstream directory
  through the `next` alias — only this shell is forked.
-->
<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext } from 'next/sidebar/provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from 'next/sidebar/useSidebarKeyboardShortcuts';
import { useCreateContact } from 'custom/dashboard/composables/zimobi/useCreateContact';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import PipelinesAPI from 'custom/dashboard/api/pipelines';
import { PIPELINES_CHANGED } from 'custom/dashboard/routes/dashboard/settings/crm/pipelineEvents';

import SidebarGroup from 'next/sidebar/SidebarGroup.vue';
import SidebarProfileMenu from 'next/sidebar/SidebarProfileMenu.vue';
import SidebarNotificationBell from 'next/sidebar/SidebarNotificationBell.vue';
import ChannelLeaf from 'next/sidebar/ChannelLeaf.vue';
import SidebarAccountSwitcher from 'next/sidebar/SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'openNotificationPanel',
  'showCreateAccountModal',
]);

const { accountId, accountScopedRoute } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

// Zimobi gates that have no route-level equivalent: the v4 sidebar derives
// visibility from `meta.permissions` / `meta.featureFlag`, which covers the
// admin-only entries but not a SuperAdmin check, an account attribute or a
// per-user flag.
const currentUser = useMapGetter('getCurrentUser');
const getAccountById = useMapGetter('accounts/getAccount');

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
// Gates the CRM group and Settings › CRM (funnels and forms), where the visit hours live. See Custom::Concerns::Account#cards_enabled?.
const isCardsEnabled = computed(
  () => !!getAccountById.value(accountId.value)?.cards
);
const cardPipelines = ref([]);
const fetchCardPipelines = async () => {
  const { data } = await PipelinesAPI.get();
  cardPipelines.value = data.filter(item => item.status === 'active');
};
// Settings › CRM changes the funnels under the sidebar, which lists them again.
useEmitter(PIPELINES_CHANGED, fetchCardPipelines);
// The account record may arrive after mount, and the cards API answers 404 while the flag is off.
watch(
  isCardsEnabled,
  enabled => {
    if (enabled) fetchCardPipelines();
  },
  { immediate: true }
);
const isContactsMenuHidden = computed(
  () => !!currentUser.value?.hide_contacts_menu
);
const isRmsPropertiesListingEnabled = computed(
  () => !!getAccountById.value(accountId.value)?.rms_properties_listing
);
const isAccountAdministrator = computed(() =>
  hasPermissions(
    ['administrator'],
    getUserPermissions(currentUser.value, accountId.value)
  )
);

// Inverted gate, straight from the v3 `createContact` entry: the button is for
// whoever CANNOT manage contacts, because those users cannot open the contacts
// list either (its routes require the same three permissions) and therefore
// never reach the "Add contact" button in its header.
const CREATE_CONTACT_HIDDEN_FOR = ['administrator', 'agent', 'contact_manage'];
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const canCreateContact = computed(() => {
  // v3 declares the CRM flag on the entry but never reaches the check; enforce
  // it here, otherwise the post-create redirect lands on a CRM-gated route.
  if (!isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CRM)) {
    return false;
  }

  return !hasPermissions(
    CREATE_CONTACT_HIDDEN_FOR,
    getUserPermissions(currentUser.value, accountId.value)
  );
});

const createNewContactDialogRef = ref(null);
const { open: openCreateContact, create: onCreateContact } = useCreateContact(
  createNewContactDialogRef
);

// The entry brings its own renderer, the same escape hatch the sidebar already
// uses for ChannelLeaf and for the label swatches. It keeps SidebarGroupHeader's
// row geometry so it belongs to the groups list, and marks itself as an action
// with a filled brand surface where a nav row would be transparent slate.
const createContactRow = () =>
  h(
    'button',
    {
      type: 'button',
      'data-testid': 'create-contact',
      class:
        'flex items-center w-full h-8 gap-2 px-2 py-1.5 rounded-lg bg-n-alpha-1 text-n-brand hover:bg-n-alpha-2',
      onClick: openCreateContact,
    },
    [
      h('span', {
        class: 'i-lucide-user-round-plus size-4 flex-shrink-0',
      }),
      h(
        'span',
        { class: 'flex-grow text-sm font-medium leading-5 text-left truncate' },
        t('SIDEBAR.CREATE_CONTACT')
      ),
    ]
  );

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation', 'lost_lead_distribution'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    ...(isCardsEnabled.value
      ? [
          {
            name: 'Cards',
            label: t('CARDS.SIDEBAR.TITLE'),
            icon: 'i-lucide-kanban',
            children: cardPipelines.value.map(item => ({
              name: `Cards ${item.id}`,
              label: item.name,
              icon: item.icon,
              to: accountScopedRoute('cards_pipeline_index', {
                pipelineId: item.id,
              }),
            })),
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    // Takes the Contacts slot for the users who cannot see that group — the v4
    // counterpart of the `createContact` action item in the v3 primary menu.
    ...(canCreateContact.value
      ? [{ name: 'Create Contact', component: createContactRow }]
      : []),
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
      ],
    },
    ...(isRmsPropertiesListingEnabled.value && isAccountAdministrator.value
      ? [
          {
            name: 'RMS Properties',
            label: t('SIDEBAR.RMS_PROPERTIES'),
            icon: 'i-lucide-building-2',
            to: accountScopedRoute('rms_properties_index'),
          },
        ]
      : []),
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
          activeOn: ['settings_teams_lead_distribution'],
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Conversation Resolution Subjects',
          label: t('SIDEBAR.CONVERSATION_RESOLUTION_SUBJECTS'),
          icon: 'i-lucide-tag',
          to: accountScopedRoute('conversation_resolution_subjects_list'),
        },
        ...(isCardsEnabled.value
          ? [
              {
                name: 'Settings CRM',
                label: t('CARDS.CRM_SETTINGS.TITLE'),
                icon: 'i-lucide-kanban',
                to: accountScopedRoute('pipelines_settings_index'),
                activeOn: [
                  'pipelines_settings_edit',
                  'forms_index',
                  'forms_edit',
                ],
              },
            ]
          : []),
        ...(isSuperAdmin.value
          ? [
              {
                name: 'Settings Client Settings',
                label: t('SIDEBAR.CLIENT_SETTINGS'),
                icon: 'i-lucide-user-cog',
                to: accountScopedRoute('client_settings_index'),
                activeOn: [
                  'client_settings_currency_migrations',
                  'client_settings_currency_migration_show',
                ],
              },
            ]
          : []),
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ].filter(
    // Zimobi: `hide_contacts_menu` removes the Contacts group for the user.
    group => !(isContactsMenuHidden.value && group.name === 'Contacts')
  );
});
</script>

<template>
  <aside
    class="w-[200px] bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak h-screen hidden sm:flex flex-col text-sm pb-1"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div class="flex items-center min-w-0 gap-2 px-2">
        <div class="grid flex-shrink-0 size-6 place-content-center">
          <Logo class="size-4" />
        </div>
        <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarAccountSwitcher
          class="flex-grow min-w-0 -mx-1"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div class="flex gap-2 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2 py-1 rounded-lg h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
      </div>
    </section>
    <nav class="grid flex-grow gap-2 px-2 pb-5 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <template v-for="item in menuItems" :key="item.name">
          <!-- Entries that bring their own renderer are action rows, not groups. -->
          <li v-if="item.component">
            <component :is="item.component" />
          </li>
          <SidebarGroup v-else v-bind="item" />
        </template>
      </ul>
    </nav>
    <section
      class="p-1 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-between gap-2 items-center"
    >
      <SidebarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
      <div class="flex items-center">
        <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarNotificationBell
          @open-notification-panel="emit('openNotificationPanel')"
        />
      </div>
    </section>
    <CreateNewContactDialog
      v-if="canCreateContact"
      ref="createNewContactDialogRef"
      @create="onCreateContact"
    />
  </aside>
</template>
